/**
 * MatchChatModal — floating chat window to talk with a matched partner
 * without leaving the Partner Match swipe deck.
 *
 * Uses the existing chat API:
 *   POST /chat/conversations            {user_id}  -> get/create conv
 *   GET  /chat/conversations/{cid}/messages
 *   POST /chat/conversations/{cid}/messages  (multipart form: text)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt, ChatCircle, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function MatchChatModal({ open, matchUser, onClose }) {
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || "";
  const avatar = matchUser?.photo_path
    ? `${API_URL}/api/users/photo/${matchUser.photo_path}`
    : (matchUser?.photo_url || matchUser?.profile_photo_url || matchUser?.avatar_url);

  const openChat = async (uid) => {
    setLoading(true);
    try {
      const r = await api.post("/chat/conversations", { user_id: uid });
      setConv(r.data);
      const m = await api.get(`/chat/conversations/${r.data.id}/messages?limit=50`);
      setMessages(m.data?.messages || []);
    } catch (e) {
      toast.error("No se pudo abrir el chat");
      onClose?.();
    } finally { setLoading(false); }
  };

  // Open conversation when the modal shows up for a new match user.
  useEffect(() => {
    if (!open || !matchUser?.id) return;
    openChat(matchUser.id);
    // eslint-disable-next-line
  }, [open, matchUser?.id]);

  // Light polling every 4s while the modal is open.
  useEffect(() => {
    if (!open || !conv?.id) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/chat/conversations/${conv.id}/messages?limit=50`);
        setMessages(r.data?.messages || []);
      } catch { /* ignore */ }
    }, 4000);
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [open, conv?.id]);

  // Auto scroll to bottom
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  // Mark as read when modal opens
  useEffect(() => {
    if (!open || !conv?.id) return;
    api.post(`/chat/conversations/${conv.id}/read`).catch(() => {});
  }, [open, conv?.id, messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || !conv?.id || sending) return;
    setSending(true);
    const temp = { id: `tmp_${Date.now()}`, text: t, sender_id: user?.id, created_at: new Date().toISOString(), _optimistic: true };
    setMessages((xs) => [...xs, temp]);
    setText("");
    try {
      const fd = new FormData();
      fd.append("text", t);
      await api.post(`/chat/conversations/${conv.id}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refresh
      const r = await api.get(`/chat/conversations/${conv.id}/messages?limit=50`);
      setMessages(r.data?.messages || []);
    } catch (e) {
      toast.error("No se pudo enviar el mensaje");
      setMessages((xs) => xs.filter((m) => m.id !== temp.id));
    } finally { setSending(false); }
  };

  const quickReplies = useMemo(() => ([
    "¡Hola! ¿Jugamos esta semana? 🎾",
    "¿En qué club prefieres jugar?",
    "¿Qué día te acomoda?",
    "¿Tienes cancha reservada?",
  ]), []);

  if (!open || !matchUser) return null;

  return (
    <div
      className="fixed inset-0 z-[250] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      data-testid="match-chat-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-[#0F0F10] border border-rose-500/30 rounded-t-3xl sm:rounded-3xl shadow-[0_30px_120px_rgba(244,63,94,0.3)] flex flex-col overflow-hidden"
           style={{ height: "min(92vh, 640px)" }}>
        {/* Header */}
        <div className="flex items-center gap-3 p-3 border-b border-white/5 bg-gradient-to-r from-rose-500/10 to-pink-500/5">
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70" aria-label="Cerrar" data-testid="match-chat-close">
            <ArrowLeft size={18} />
          </button>
          {avatar ? (
            <img src={avatar} alt={matchUser.name} className="w-10 h-10 rounded-full object-cover border-2 border-rose-500" />
          ) : (
            <div className="w-10 h-10 rounded-full border-2 border-rose-500 bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center font-black text-white">
              {(matchUser.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white truncate leading-none">{matchUser.name}</p>
            <p className="text-[11px] text-rose-300/80">💘 Partner Match</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70 sm:hidden" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#0A0A0A]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-white/60 gap-2 py-6">
              <ChatCircle size={40} weight="duotone" className="text-rose-500/40" />
              <p className="text-sm">Rompe el hielo con {matchUser.name?.split(" ")[0]} 👋</p>
              <p className="text-[11px] text-white/40">Pregúntale cuándo juega, en qué club o manda un saludo.</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-rose-500 text-white rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"} ${m._optimistic ? "opacity-60" : ""}`}>
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Quick replies (only when empty) */}
        {messages.length === 0 && !loading && (
          <div className="px-3 pb-2 flex gap-1.5 flex-wrap">
            {quickReplies.map((q, i) => (
              <button
                key={i}
                onClick={() => setText(q)}
                className="px-2.5 py-1 rounded-full border border-rose-500/40 text-rose-300 text-[11px] hover:bg-rose-500/10 transition-colors"
                data-testid={`match-chat-quick-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Composer */}
        <div className="p-3 border-t border-white/5 bg-[#0F0F10] flex items-center gap-2" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Escribe un mensaje..."
            className="flex-1 bg-black/40 border border-white/15 rounded-full px-4 h-11 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-rose-500"
            disabled={!conv?.id || sending}
            data-testid="match-chat-input"
          />
          <button
            onClick={send}
            disabled={!text.trim() || !conv?.id || sending}
            className="w-11 h-11 rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors shrink-0"
            aria-label="Enviar"
            data-testid="match-chat-send"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
